import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, RefreshCw, Search, TriangleAlert } from "lucide-react";

import {
  appsApi,
  type DatasetFilterCondition,
  type DatasetFilterOp,
  type DatasetGrant,
  type DatasetRowFilter,
} from "@/lib/api-apps";
import { datasetsApi, type DatasetFieldInfo } from "@/lib/api-datasets";

/**
 * Per-(role, EXTERNAL dataset) rows + fields editor (ADR-0028 N4) — the dataset
 * twin of `PolicyControls`, but LEANER: an external dataset is read-only (no
 * write matrix) and carries no Tray identity, so a row filter value is a literal
 * OR a logical key `$user.<key>` (NOT `$me`). Persists into the role's
 * `assistant_scope.dataset_grants[<datasetId>]` via the `onSave` callback (the
 * parent rebuilds the scope + PATCHes the role). Shown only when granted.
 *
 * Fields = "nascondi-per-eccezione": an external table has 100+ columns, but the
 * task is "hide a few", so the editor is a SEARCHABLE list with a Tutti/Solo
 * nascosti toggle — never a wall of N selects — and each field shows its L2
 * semantic LABEL so the operator recognises a cryptic name (CDS_RAGSOC →
 * "Ragione sociale").
 *
 * Two guards mirror the backend `validate_grants`, applied client-side so a bad
 * combo never round-trips to a 422:
 *  - the row-filter field dropdown EXCLUDES hidden fields (hidden ∩ filter = ∅);
 *  - hiding the field the current filter references CLEARS the filter.
 */

const PRESENCE_OPS = new Set<DatasetFilterOp>(["is_null", "is_not_null"]);
const USER_PREFIX = "$user.";

type RowMode = "all" | "custom";
type FieldMode = "hidden" | "all";

function emptyDraft(field: string): DatasetFilterCondition {
  return { field, op: "eq", value: "" };
}

/** The human meaning of a field, if the L2 layer annotated it. */
function meaningOf(f: DatasetFieldInfo): string | null {
  return f.label || f.semantic_type || null;
}

/** Build a grant from the current (hidden, rowFilter); an empty grant → null so
 *  the parent drops the dataset_grants entry entirely. */
function buildGrant(hidden: string[], rowFilter: DatasetRowFilter | null): DatasetGrant | null {
  const out: DatasetGrant = {};
  if (hidden.length) out.hidden = [...hidden].sort();
  if (rowFilter) out.row_filter = rowFilter;
  return out.hidden || out.row_filter ? out : null;
}

export function DatasetPolicyControls({
  appId,
  datasetId,
  grant,
  disabled,
  onSave,
}: {
  appId: string;
  datasetId: string;
  grant: DatasetGrant | undefined;
  disabled: boolean;
  onSave: (grant: DatasetGrant | null) => void;
}) {
  const { t } = useTranslation();

  // Fail fast: an unreachable external source times out (~10s); the default 3×
  // retry would hang the panel for ~30s. One attempt, then an explicit error.
  const fieldsQ = useQuery({
    queryKey: ["datasets", datasetId, "fields"],
    queryFn: () => datasetsApi.fields(datasetId),
    retry: false,
  });
  // Locked profile fields back a `$user.<key>` filter (an editable one is a
  // self-grant the backend rejects — we only offer locked keys).
  const profileQ = useQuery({
    queryKey: ["apps", appId, "profile-fields"],
    queryFn: () => appsApi.listProfileFields(appId),
  });

  const fields = fieldsQ.data ?? [];
  const lockedKeys = (profileQ.data ?? []).filter((f) => !f.user_editable);

  const hidden = React.useMemo(() => new Set(grant?.hidden ?? []), [grant]);
  const rowFilter = grant?.row_filter ?? null;
  const storedCond = rowFilter?.conditions?.[0];

  const [rowMode, setRowMode] = React.useState<RowMode>(rowFilter ? "custom" : "all");
  const [draft, setDraft] = React.useState<DatasetFilterCondition>(storedCond ?? emptyDraft(""));
  // Default to the compact hidden-only view once there ARE hidden fields; a fresh
  // grant opens on "Tutti" so you can browse + hide without searching first.
  const [fieldMode, setFieldMode] = React.useState<FieldMode>(hidden.size ? "hidden" : "all");
  const [q, setQ] = React.useState("");

  // Re-seed when the selected dataset/role changes (the grant identity flips).
  React.useEffect(() => {
    setRowMode(rowFilter ? "custom" : "all");
    setDraft(storedCond ?? emptyDraft(""));
    setFieldMode(hidden.size ? "hidden" : "all");
    setQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, grant]);

  // Fields a row filter may reference = visible fields (hidden are disjoint).
  const filterableFields = fields.filter((f) => !hidden.has(f.name));

  function persist(nextHidden: Set<string>, nextFilter: DatasetRowFilter | null) {
    onSave(buildGrant([...nextHidden], nextFilter));
  }

  function toggleField(name: string, hide: boolean) {
    const next = new Set(hidden);
    if (hide) next.add(name);
    else next.delete(name);
    // Hiding the field the current filter references would fail the backend's
    // disjoint guard — clear the filter in that case (mirror it in local state).
    let nf = rowFilter;
    if (hide && rowFilter?.conditions?.some((c) => c.field === name)) {
      nf = null;
      setRowMode("all");
    }
    persist(next, nf);
  }

  function applyFilter() {
    const field = draft.field || filterableFields[0]?.name;
    if (!field) return;
    const cond: DatasetFilterCondition = PRESENCE_OPS.has(draft.op)
      ? { field, op: draft.op }
      : { field, op: draft.op, value: draft.value ?? "" };
    persist(hidden, { match: "all", conditions: [cond] });
  }

  function changeRowMode(mode: RowMode) {
    setRowMode(mode);
    if (mode === "all") persist(hidden, null);
    else if (!draft.field && filterableFields[0]) setDraft(emptyDraft(filterableFields[0].name));
  }

  const isPresence = PRESENCE_OPS.has(draft.op);
  const usesUser = (draft.value ?? "").startsWith(USER_PREFIX);
  const sel = "rounded border border-border bg-background px-2 py-1";

  // The field list shown in the Fields section: search always wins (so you can
  // find ANYTHING to hide); otherwise the toggle decides the resting view.
  const ql = q.trim().toLowerCase();
  const shownFields = React.useMemo(() => {
    if (ql) {
      return fields.filter(
        (f) => f.name.toLowerCase().includes(ql) || (meaningOf(f) ?? "").toLowerCase().includes(ql),
      );
    }
    if (fieldMode === "hidden") return fields.filter((f) => hidden.has(f.name));
    return fields;
  }, [fields, ql, fieldMode, hidden]);

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-surface/50 p-2 text-xs">
      {/* Rows ------------------------------------------------------------ */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-muted-foreground">{t("apps.policyRows", "Rows")}</span>
        <select
          aria-label={t("apps.policyRows", "Rows")}
          className={sel}
          value={rowMode}
          disabled={disabled}
          onChange={(e) => changeRowMode(e.target.value as RowMode)}
        >
          <option value="all">{t("apps.policyAll", "All rows")}</option>
          <option value="custom">{t("apps.policyCustom", "Custom filter…")}</option>
        </select>
      </div>

      {rowMode === "custom" && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-surface p-2">
          <span className="text-muted-foreground">{t("apps.policyWhere", "Show rows where")}</span>
          <select
            aria-label={t("apps.policyField", "Field")}
            className={sel}
            value={draft.field}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, field: e.target.value })}
          >
            {filterableFields.map((f) => {
              const m = meaningOf(f);
              return (
                <option key={f.name} value={f.name}>
                  {f.name}
                  {m ? ` — ${m}` : ""}
                </option>
              );
            })}
          </select>
          <select
            aria-label={t("apps.policyOperator", "Operator")}
            className={sel}
            value={draft.op}
            disabled={disabled}
            onChange={(e) => {
              const op = e.target.value as DatasetFilterOp;
              // presence ops carry no value — drop it from the draft
              setDraft((d) =>
                PRESENCE_OPS.has(op) ? { field: d.field, op } : { ...d, op, value: d.value ?? "" },
              );
            }}
          >
            <option value="eq">{t("apps.policyOpEq", "equals (=)")}</option>
            <option value="ne">{t("apps.policyOpNeq", "is not (≠)")}</option>
            <option value="contains">{t("apps.policyOpContains", "contains")}</option>
            <option value="is_null">{t("apps.policyOpIsEmpty", "is empty")}</option>
            <option value="is_not_null">{t("apps.policyOpIsNotEmpty", "is not empty")}</option>
          </select>
          {!isPresence && (
            <>
              <select
                aria-label={t("apps.policyValueSource", "Value source")}
                className={sel}
                value={usesUser ? "user" : "literal"}
                disabled={disabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    value: e.target.value === "user" ? `${USER_PREFIX}${lockedKeys[0]?.key ?? ""}` : "",
                  })
                }
              >
                <option value="literal">{t("apps.policyLiteral", "a fixed value")}</option>
                <option value="user" disabled={lockedKeys.length === 0}>
                  {t("apps.policyUserAttr", "the user's attribute")}
                </option>
              </select>
              {usesUser ? (
                <select
                  aria-label={t("apps.policyUserAttr", "the user's attribute")}
                  className={sel}
                  value={(draft.value ?? "").slice(USER_PREFIX.length)}
                  disabled={disabled}
                  onChange={(e) => setDraft({ ...draft, value: `${USER_PREFIX}${e.target.value}` })}
                >
                  {lockedKeys.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ($user.{f.key})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={sel}
                  placeholder={t("apps.policyValue", "value")}
                  value={draft.value ?? ""}
                  disabled={disabled}
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                />
              )}
            </>
          )}
          <button
            type="button"
            className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-accent-foreground disabled:opacity-50"
            disabled={disabled || filterableFields.length === 0}
            onClick={applyFilter}
          >
            {t("apps.apply", "Apply")}
          </button>
        </div>
      )}
      {lockedKeys.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t(
            "apps.datasetNoLockedAttr",
            "Declare a locked profile field (Members & roles) to filter rows by the user's own attribute.",
          )}
        </p>
      )}

      {/* Fields ---------------------------------------------------------- */}
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="font-medium text-muted-foreground">{t("apps.policyFields", "Fields")}</span>
          {fields.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">
                {fields.length - hidden.size} {t("apps.fieldVisibleCount", "visible")} ·{" "}
                <span className={hidden.size ? "text-warning" : undefined}>
                  {hidden.size} {t("apps.fieldHiddenCount", "hidden")}
                </span>
              </span>
              <div className="inline-flex overflow-hidden rounded-md border border-border/60">
                {(["all", "hidden"] as FieldMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={
                      "px-2 py-0.5 text-[11px] " +
                      (fieldMode === m ? "bg-accent text-accent-foreground" : "text-muted-foreground")
                    }
                    onClick={() => setFieldMode(m)}
                  >
                    {m === "all" ? t("apps.fieldsAll", "All") : t("apps.fieldsHiddenOnly", "Hidden only")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {fieldsQ.isError ? (
          <div className="flex items-center gap-2 rounded-md border border-border/60 bg-surface p-2 text-[11px] text-muted-foreground">
            <TriangleAlert className="h-3.5 w-3.5 text-warning" strokeWidth={1.75} />
            <span>{t("apps.datasetSourceUnreachable", "Source unreachable — could not read its fields.")}</span>
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5"
              onClick={() => fieldsQ.refetch()}
            >
              <RefreshCw className="h-3 w-3" strokeWidth={1.75} />
              {t("apps.retry", "Retry")}
            </button>
          </div>
        ) : fieldsQ.isLoading ? (
          <p className="text-[11px] text-muted-foreground">{t("apps.loading", "Loading…")}</p>
        ) : fields.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            {t("apps.datasetNoFields", "The source schema is unavailable.")}
          </p>
        ) : (
          <>
            <div className="mb-1.5 flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2 py-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} aria-hidden />
              <input
                aria-label={t("apps.fieldSearch", "Search fields")}
                className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                placeholder={t("apps.fieldSearchPlaceholder", "Search a field to hide…")}
                value={q}
                disabled={disabled}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {shownFields.length === 0 ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">
                {ql
                  ? t("apps.fieldNoMatch", "No field matches.")
                  : t("apps.fieldNoneHidden", "No hidden field. Search a field to hide it, or switch to «All».")}
              </p>
            ) : (
              <div className="max-h-56 space-y-0.5 overflow-y-auto pr-1">
                {shownFields.map((f) => {
                  const isHidden = hidden.has(f.name);
                  const m = meaningOf(f);
                  return (
                    <div
                      key={f.name}
                      className="flex items-center justify-between gap-2 rounded px-1.5 py-1 hover:bg-surface"
                    >
                      <div className="min-w-0 truncate">
                        <span className="font-mono text-[11px]">{f.name}</span>
                        {m && <span className="ml-2 text-muted-foreground">{m}</span>}
                      </div>
                      <button
                        type="button"
                        aria-label={f.name}
                        title={isHidden ? t("apps.fieldHidden", "Hidden") : t("apps.fieldVisible", "Visible")}
                        className={
                          "shrink-0 rounded p-1 " +
                          (isHidden ? "text-warning" : "text-muted-foreground hover:text-foreground")
                        }
                        disabled={disabled}
                        onClick={() => toggleField(f.name, !isHidden)}
                      >
                        {isHidden ? (
                          <EyeOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                        ) : (
                          <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
